import { sceneState, type SceneState } from '@/lib/threeScene/SceneContext';
import { renderingState, type RenderingState } from '@/lib/threeScene/renderingState';
import { gridNode, type GridNode } from '@/lib/threeScene/nodes/grid';
import { imagePlaneNode, type ImagePlaneNode } from '@/lib/threeScene/nodes/imagePlane';
import { vector3 } from '@/domain/geometry/vector3';
import type { XRaySceneNode, XRayViewportId } from '@/views/dicoms/xrays/xrayScene';
import type { ElementSize } from '@/util/observeElementSize';
import { type Vector3 } from 'three';
import { type AxesNode, axesNode } from '@/lib/threeScene/nodes/axes';
import { createImageViewerCameraState } from '@/lib/threeScene/cameras/imageViewerCamera';

/**
 * Distance along +Z from the look-at point to the X-ray camera position.
 * Shared by every X-ray scene (main viewer, hip-spine xray).
 */
export const XRAY_CAMERA_TARGET_DISTANCE = 1000 as const;

/**
 * Frustum height for an X-ray orthographic camera, in world units. Held at 1
 * so the frustum width equals the viewport aspect ratio (`width / height`);
 * image fit is then driven entirely by camera zoom.
 */
export const XRAY_CAMERA_FRUSTUM_HEIGHT = 1 as const;

export type XRaySceneState = SceneState<XRaySceneNode> & {
    readonly viewportId: XRayViewportId;
    rendering: RenderingState;
    nodes: {
        axes: AxesNode;
        grid: GridNode;
        imagePlane: ImagePlaneNode;
    };
};

export function xraySceneState(viewportId: XRayViewportId): XRaySceneState {
    return {
        ...sceneState<XRaySceneNode>(),
        viewportId,
        rendering: renderingState({
            camera: createImageViewerCameraState({
                position: vector3(0, 0, XRAY_CAMERA_TARGET_DISTANCE),
                up: vector3(0, 1, 0),
                height: XRAY_CAMERA_FRUSTUM_HEIGHT,
                enableControls: viewportId === 'main',
                leftMouseButton: 'pan',
            }),
        }),
        nodes: {
            axes: axesNode('origin', { visible: false }),
            grid: gridNode('reference-grid', { visible: false }),
            imagePlane: imagePlaneNode('xray-image', { visible: false }),
        },
    };
}

/**
 * Get the camera parameters that show a full X-ray image of a given size in
 * a given size of viewport. It is presumed that the image-plane is created so that:
 * - It is the same width and height as the image.
 * - Its bottom-left corner is at the origin.
 */
export function fullViewCameraParams(
    imageSize: ElementSize,
    viewportSize: ElementSize,
): { target: Vector3; zoom: number } {
    // The camera target is in the centre of the image
    const target = vector3(imageSize.width / 2, imageSize.height / 2, 0);

    const imageAspect = imageSize.width / Math.max(imageSize.height, 1);
    const viewportAspect = viewportSize.width / Math.max(viewportSize.height, 1);
    if (viewportAspect > imageAspect) {
        // The viewport is wider than the image, so set the zoom to fit the image height
        // into the viewport
        return {
            target,
            zoom: XRAY_CAMERA_FRUSTUM_HEIGHT / Math.max(imageSize.height, 1),
        };
    } else {
        // The viewport is narrower than the image, so fit the image width into the viewport
        return {
            target,
            zoom: (XRAY_CAMERA_FRUSTUM_HEIGHT * viewportAspect) / Math.max(imageSize.width, 1),
        };
    }
}
